import { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { Box, Button, Grid } from '@mui/material';

import Yup from '../../../../utils/yup';
import { AutoCompleteSelectUI, DialogUI, FileUploadUI, FormErrorUI, FormLabelUI } from '../../../Interface';
import TodoDialogFields from './CrmDialogFields';
import useUsers from '../../../../context/UsersContext';
import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';
import crmTaskService from '../../../../service/crmTask.service';
import dayjs from 'dayjs';

const CrmTaskDialog = ({ dialogData, handleClose, autoCompleteOptions, autoCompleteData, setRefetch }) => {
  const { user } = useUsers();
  const { open, projectId, companyId } = dialogData;

  const defValues = {
    name: '',
    typeId: null,
    statusId: null,
    eventType: '',
    startDate: dayjs(new Date()),
    endDate: dayjs(new Date()).add(1, 'hour'),
    duration: 1,
    crmTaskAttachment: [],
  };
  const [initValues, setInitValues] = useState(defValues);

  useEffect(() => {
    if (!open) return;

    setInitValues({
      ...defValues,
      typeId: autoCompleteData?.crmTaskType?.defaultValue,
      statusId: autoCompleteData?.crmTaskStatus?.defaultValue,
    });
  }, [open]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    typeId: Yup.string().nullable(),
    statusId: Yup.string().nullable(),
    eventType: Yup.string().nullable(),
    startDate: Yup.string().required(),
    endDate: Yup.string().required(),
  });

  const handleCreate = (values) => {
    if (!companyId) {
      addNotification({
        content: 'Az esemény nem hozható létre, mert a projekthez nincs cég rendelve.',
        type: NotificationType.WARNING,
      });
      return;
    }

    const insertObject = {
      ...values,
      duration: parseFloat(values.duration),
      startDate: dayjs(values.startDate).toISOString(),
      endDate: dayjs(values.endDate).toISOString(),
      createdBy: user.userId,
      projectId,
      companyId,
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObject));

    if (insertObject.crmTaskAttachment) {
      for (const file of insertObject.crmTaskAttachment) {
        formData.append('files', file);
      }
    }

    crmTaskService.createTask(formData).then(() => {
      addNotification({
        content: 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });
      setRefetch(true);
      handleClose();
    });
  };

  return (
    <DialogUI
      open={open}
      onClose={handleClose}
      headerContent={'Új esemény hozzáadása'}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '800px',
          },
        },
        '& .MuiDialogContent-root': {
          px: 2,
          pb: 0,
        },
      }}
    >
      <Box mt={2}>
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleCreate}
          enableReinitialize
        >
          {({ values, setFieldValue, errors, validateField }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12} md={9}>
                  <TodoDialogFields autoCompleteOptions={autoCompleteOptions} />

                  <FileUploadUI
                    rootSx={{ m: 0, mt: 2 }}
                    value={values.crmTaskAttachment}
                    onFilesChange={(files) =>
                      setFieldValue(
                        'crmTaskAttachment',
                        files.map(({ file }) => file)
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button type="submit" fullWidth variant="contained" sx={{ mb: 3 }}>
                    {'Hozzáadás'}
                  </Button>
                  <Field
                    type="autocomplete"
                    name="statusId"
                    component={AutoCompleteSelectUI}
                    fullWidth
                    label={<FormLabelUI text="Státusz" />}
                    variant="outlined"
                    size="small"
                    helperText={<FormErrorUI message={errors.statusId} />}
                    onChange={(_e, newVal) => {
                      setFieldValue('statusId', newVal?.value ?? null).then(() => {
                        validateField('statusId');
                      });
                    }}
                    InputLabelProps={{ shrink: values.statusId !== '' }}
                    selectedValue={values.statusId}
                    dataset={autoCompleteOptions?.crmTaskStatus}
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </DialogUI>
  );
};

export default CrmTaskDialog;
